import { Button } from '@/components/ui/button';
import { UnifiedContextMenuTrigger } from '@/components/ui/UnifiedContextMenu';
import { cn } from '@/lib/utils';
import { CellContext } from '@tanstack/react-table';
import {
  CircleChevronRight,
  Ellipsis,
  Square,
  SquareCheckBig,
} from 'lucide-react';

/**
 * TODO: Refactor action cell styling for better maintainability
 *
 * Current issues:
 * - Complex CSS with many state selectors duplicated in TableActionHeaderCell
 * - Background gradient approach could be simplified
 *
 * Proposed improvements:
 * - Extract shared styling patterns to reusable utility
 * - Replace gradient with opaque button backgrounds + separate gradient-transition element
 * - Consolidate state selector logic between ActionColumnCell and ActionHeaderCell
 */

export function TableColumnActionCell<TData, TValue>(
  props: CellContext<TData, TValue> & {
    /**
     * When set, renders an always-visible drill disclosure button (circled
     * chevron, iOS detail-disclosure convention). Only supplied for rows the
     * table has decided carry a secondary drill target in expand-primary
     * tree mode (see DataTable's tree.hasSecondaryDrill).
     */
    onDrill?: (data: TData) => void;
  },
): any {
  return (
    <div className="absolute inset-0 flex items-stretch justify-end gap-1 align-middle">
      <div
        className={cn(
          'flex grow-0 pl-1',
          'opacity-0 transition duration-200',
          'to-card bg-gradient-to-r from-transparent to-10%',
          'md:group-hover/row:opacity-90',
          'group-data-[state=selected]/row:to-muted',
          'md:group-hover/row:to-(--muted-half)',
          'md:group-data-[state=selected]/row:group-hover/row:to-muted',
          'group-data-[select-mode=active]/table:opacity-90',
          'aria-expanded:opacity-100',
        )}
      >
        {/* Ellipsis button - opens unified menu */}
        <UnifiedContextMenuTrigger>
          <Button
            variant="ghost"
            size="icon"
            tabIndex={-1}
            className={cn(
              'h-full',
              // Hide on mobile (actions menu accessed via long-press)
              'hidden md:flex',
              'w-0 overflow-clip opacity-0 transition-opacity duration-200',
              'md:group-hover/row:w-8 md:group-hover/row:opacity-40',
              'aria-expanded:w-8 aria-expanded:opacity-100',
              'group-data-[active=active]/row:opacity-40',
              'hover:cursor-pointer hover:opacity-100',
            )}
          >
            <Ellipsis />
          </Button>
        </UnifiedContextMenuTrigger>

        {/* Checkbox button - shown differently on mobile vs desktop */}
        {/* might have we overcomplicated this? */}
        <Button
          tabIndex={-1}
          variant="ghost"
          className={cn(
            // Larger tap target on mobile (48px min), standard on desktop (32px)
            'h-full w-12 p-0 md:w-8',
            // Center the icon within the button
            'flex items-center justify-center',
            // Mobile: hidden by default, show only when selection active
            'hidden group-data-[select-mode=active]/table:flex',
            // Desktop: use opacity, show on hover or when selection active
            'md:flex md:opacity-0 md:transition-opacity md:duration-200',
            'md:group-data-[select-mode=active]/table:opacity-100',
            'md:group-hover/row:opacity-40',
            'md:group-data-[active=active]/row:opacity-40',
            'md:hover:cursor-pointer md:hover:opacity-100',
          )}
          onClick={(e) => {
            e.stopPropagation();
            props.row.getToggleSelectedHandler()(e);
          }}
          aria-label={props.row.getIsSelected() ? 'Deselect row' : 'Select row'}
        >
          {props.row.getIsSelected() ? <SquareCheckBig /> : <Square />}
        </Button>
      </div>

      {/* Drill disclosure button — shown only for rows the table flagged
          with a secondary drill target (onDrill supplied). Gradient underlay
          matches the hover actions so it stays legible over wide content. */}
      {props.onDrill && (
        <div
          className={cn(
            'to-card flex grow-0 items-center bg-gradient-to-r from-transparent to-10% pl-1',
            'group-data-[state=selected]/row:to-muted',
            'md:group-hover/row:to-(--muted-half)',
            'md:group-data-[state=selected]/row:group-hover/row:to-muted',
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            tabIndex={-1}
            aria-label="Open"
            className={cn(
              'text-muted-foreground hover:text-foreground h-full w-8',
              'opacity-60 transition-opacity duration-200 hover:cursor-pointer',
              'hover:opacity-100 md:group-hover/row:opacity-100',
            )}
            onClick={(e) => {
              e.stopPropagation();
              props.onDrill!(props.row.original);
            }}
          >
            <CircleChevronRight />
          </Button>
        </div>
      )}
    </div>
  );
}
